
import React, { useMemo, useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const emptyForm = () => ({
    name: '',
    travel_order_id: '',
    trip_component_id: '',
    package_component_id: '',
    merchant_allocation_id: '',
    merchant_id: '',
    pax_count: '',
    service_start: '',
    service_end: '',
    unit_price: '',
    status: 'Waiting For Merchant Acknowledgment',
    merchant_confirmation_no: '',
    merchant_notes: '',
    cancellation_date: ''
});

const autocompleteSlotProps = {
    popper: { sx: { zIndex: 1400 } },
};

/** Same matching rules as trip Allocations merchant lookup. */
const CATEGORY_TO_SERVICE_TYPES = {
    accommodation: ['accommodation'],
    transportation: ['transport', 'transfer', 'transportation'],
    activity: ['activity'],
    meal: ['meal', 'meals'],
    guide: ['guide'],
    other: ['other', 'rental'],
};

const CATEGORY_TO_ASSET_TYPES = {
    accommodation: ['accommodation'],
    transportation: ['transport', 'transfer'],
    activity: ['activity'],
    meal: ['meal', 'meals'],
    guide: ['guide'],
    other: ['other', 'rental'],
};

const normalizeToken = (value) => String(value || '').toLowerCase().replace(/[\s_-]+/g, '');

const tokenMatchesAllowed = (value, allowed) => {
    const type = normalizeToken(value);
    if (!type) return false;
    return allowed.some((token) => type.includes(token) || token.includes(type));
};

const serviceTypeMatchesCategory = (serviceType, category) => {
    const cat = normalizeToken(category);
    if (!cat) return false;
    const allowed = CATEGORY_TO_SERVICE_TYPES[cat] || [cat];
    return tokenMatchesAllowed(serviceType, allowed);
};

const assetTypeMatchesCategory = (assetType, category) => {
    const cat = normalizeToken(category);
    if (!cat) return false;
    const allowed = CATEGORY_TO_ASSET_TYPES[cat] || [cat];
    return tokenMatchesAllowed(assetType, allowed);
};

const merchantHasDmc = (merchant) => {
    if (merchant?.isDmc) return true;
    const serviceTypes = merchant?.serviceTypes || [];
    return serviceTypes.some((value) => normalizeToken(value) === 'dmc');
};

const accountHasDmcService = (account) => {
    const raw = account?.service_type;
    const list = Array.isArray(raw)
        ? raw
        : (raw ? String(raw).split(',') : []);
    return list.some((value) => normalizeToken(value) === 'dmc');
};

const merchantMatchesLogistics = (merchant, { logisticsId, category, currentMerchantId }) => {
    if (currentMerchantId != null && currentMerchantId !== ''
        && String(merchant.id) === String(currentMerchantId)) {
        return true;
    }
    if (merchantHasDmc(merchant)) return true;
    const serviceTypes = merchant.serviceTypes || [];
    if (category && serviceTypes.some((st) => serviceTypeMatchesCategory(st, category))) {
        return true;
    }
    const linkedIds = merchant.logisticsIds || [];
    if (logisticsId && linkedIds.some((id) => String(id) === String(logisticsId))) {
        return true;
    }
    const assetTypes = merchant.assetTypes || [];
    return category
        ? assetTypes.some((assetType) => assetTypeMatchesCategory(assetType, category))
        : false;
};

const NewMerchantOrderLineModal = ({
    isOpen,
    onClose,
    onSave,
}) => {
    const { axiosInstance } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState(emptyForm());
    const [formErrors, setFormErrors] = useState({});
    const [packageComponents, setPackageComponents] = useState([]);
    const [travelOrders, setTravelOrders] = useState([]);
    const [merchantAllocations, setMerchantAllocations] = useState([]);
    const [merchants, setMerchants] = useState([]);
    const [loadingPackageComponents, setLoadingPackageComponents] = useState(false);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [loadingAllocations, setLoadingAllocations] = useState(false);
    const [loadingMerchants, setLoadingMerchants] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            fetchPackageComponents();
            fetchTravelOrders();
            fetchMerchantAllocations();
            fetchMerchants();
        }
    }, [isOpen]);

    const fetchTravelOrders = async () => {
        setLoadingOrders(true);
        try {
            const response = await axiosInstance.get('/travel-orders/');
            setTravelOrders(response.data || []);
        } catch (error) {
            console.error('Error fetching travel orders:', error);
        } finally {
            setLoadingOrders(false);
        }
    };

    const fetchPackageComponents = async () => {
        setLoadingPackageComponents(true);
        try {
            const response = await axiosInstance.get('/merchant-assets/trip-logistics-options');
            setPackageComponents(response.data || []);
        } catch (error) {
            console.error('Error fetching trip logistics:', error);
        } finally {
            setLoadingPackageComponents(false);
        }
    };

    const fetchMerchantAllocations = async () => {
        setLoadingAllocations(true);
        try {
            const response = await axiosInstance.get('/merchant-allocations/', { params: { limit: 1000 } });
            setMerchantAllocations(response.data || []);
        } catch (error) {
            console.error('Error fetching merchant allocations:', error);
        } finally {
            setLoadingAllocations(false);
        }
    };

    const fetchMerchants = async () => {
        setLoadingMerchants(true);
        try {
            const [accountsRes, assetsRes] = await Promise.all([
                axiosInstance.get('/accounts/', { params: { account_type: 'Partner' } }),
                axiosInstance.get('/merchant-assets/', { params: { limit: 5000 } }).catch(() => ({ data: [] })),
            ]);
            const partners = Array.isArray(accountsRes.data) ? accountsRes.data : [];
            const merchantAccounts = partners.filter((account) => {
                // Include Merchant, Guide, DMC, etc. — not only partner_type "merchant".
                const partnerType = String(account?.partner_type || '').trim().toLowerCase();
                if (!partnerType) return true;
                return (
                    partnerType === 'merchant'
                    || partnerType.includes('merchant')
                    || partnerType === 'guide'
                    || partnerType.includes('guide')
                    || partnerType === 'dmc'
                    || partnerType.includes('dmc')
                );
            });

            const assetsByMerchant = new Map();
            (Array.isArray(assetsRes.data) ? assetsRes.data : []).forEach((asset) => {
                if (!asset?.merchant_id) return;
                const entry = assetsByMerchant.get(asset.merchant_id) || {
                    logisticsIds: new Set(),
                    assetTypes: new Set(),
                };
                if (asset.package_component_id != null && asset.package_component_id !== '') {
                    entry.logisticsIds.add(Number(asset.package_component_id));
                }
                if (asset.asset_type) {
                    entry.assetTypes.add(asset.asset_type);
                }
                assetsByMerchant.set(asset.merchant_id, entry);
            });

            setMerchants(
                merchantAccounts.map((account) => {
                    const meta = assetsByMerchant.get(account.id);
                    const serviceTypes = Array.isArray(account.service_type)
                        ? account.service_type
                        : (account.service_type ? String(account.service_type).split(',') : []);
                    return {
                        ...account,
                        serviceTypes: serviceTypes.map((value) => String(value).trim()).filter(Boolean),
                        isDmc: accountHasDmcService(account),
                        logisticsIds: meta ? Array.from(meta.logisticsIds) : [],
                        assetTypes: meta ? Array.from(meta.assetTypes) : [],
                    };
                })
            );
        } catch (error) {
            console.error('Error fetching merchants:', error);
            setMerchants([]);
        } finally {
            setLoadingMerchants(false);
        }
    };

    const selectedOrder = useMemo(
        () => travelOrders.find((order) => String(order.id) === String(formData.travel_order_id)) || null,
        [travelOrders, formData.travel_order_id]
    );

    const tripLogisticsOptions = useMemo(() => {
        const packageId = selectedOrder?.trip_package_id;
        return (packageComponents || [])
            .filter((item) => !packageId || String(item.trip_package_id || '') === String(packageId))
            .map((item) => ({
                value: String(item.id),
                label: item.label || item.name || `Logistics #${item.id}`,
                trip_package_id: item.trip_package_id ? String(item.trip_package_id) : '',
                name: item.name,
                category: item.category || '',
            }));
    }, [packageComponents, selectedOrder]);

    const selectedTripLogistics =
        tripLogisticsOptions.find(
            (opt) => String(opt.value) === String(formData.package_component_id || '')
        ) || null;

    const filteredMerchants = useMemo(() => {
        const logisticsId = formData.package_component_id;
        if (!logisticsId) return [];
        const category = selectedTripLogistics?.category || '';
        return (merchants || []).filter((merchant) =>
            merchantMatchesLogistics(merchant, {
                logisticsId,
                category,
                currentMerchantId: formData.merchant_id,
            })
        );
    }, [merchants, formData.package_component_id, formData.merchant_id, selectedTripLogistics]);

    const allocationOptions = useMemo(() => {
        const packageId = selectedOrder?.trip_package_id;
        const departureId = selectedOrder?.departure_date_id;
        const logisticsId = formData.package_component_id;
        return (merchantAllocations || []).filter((alloc) => {
            if (alloc.status === 'Rejected' || alloc.is_active === false) return false;
            if (packageId && alloc.trip_package_id && String(alloc.trip_package_id) !== String(packageId)) {
                return false;
            }
            if (departureId && alloc.departure_date_id && String(alloc.departure_date_id) !== String(departureId)) {
                return false;
            }
            if (logisticsId && alloc.package_component_id && String(alloc.package_component_id) !== String(logisticsId)) {
                return false;
            }
            return true;
        });
    }, [merchantAllocations, selectedOrder, formData.package_component_id]);

    const orderOptions = useMemo(
        () =>
            (travelOrders || []).map((order) => ({
                value: String(order.id),
                label: order.package
                    ? `${order.name} — ${order.package}`
                    : (order.name || order.order_id || `Order #${order.id}`),
                order,
            })),
        [travelOrders]
    );

    const selectedOrderOption =
        orderOptions.find((opt) => String(opt.value) === String(formData.travel_order_id || '')) || null;

    const applyOrder = (order) => {
        setFormData((prev) => {
            const next = {
                ...prev,
                travel_order_id: order ? String(order.id) : '',
                package_component_id: '',
                merchant_allocation_id: '',
                trip_component_id: '',
                merchant_id: '',
            };
            if (order?.travellers && !prev.pax_count) {
                next.pax_count = String(order.travellers);
            }
            return next;
        });
        setFormErrors((prev) => ({
            ...prev,
            travel_order_id: '',
            package_component_id: '',
            merchant_allocation_id: '',
            merchant_id: '',
        }));
    };

    const applyAllocation = (alloc) => {
        setFormData((prev) => {
            const next = {
                ...prev,
                merchant_allocation_id: alloc ? String(alloc.id) : '',
            };
            if (!alloc) return next;
            if (alloc.merchant_id) next.merchant_id = String(alloc.merchant_id);
            if (alloc.package_component_id) next.package_component_id = String(alloc.package_component_id);
            if (alloc.component_id) next.trip_component_id = String(alloc.component_id);
            if ((alloc.cost || alloc.cost === 0) && !prev.unit_price) {
                next.unit_price = String(alloc.cost);
            }
            return next;
        });
        setFormErrors((prev) => ({
            ...prev,
            merchant_allocation_id: '',
            package_component_id: '',
            merchant_id: '',
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setFormErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.travel_order_id) errors.travel_order_id = 'Order / Booking is required';
        if (!formData.package_component_id) errors.package_component_id = 'Trip Logistics is required';
        if (!formData.pax_count) errors.pax_count = 'Pax Count is required';
        if (!formData.unit_price) errors.unit_price = 'Unit Price is required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                name: formData.name || undefined,
                travel_order_id: parseInt(formData.travel_order_id, 10),
                trip_component_id: formData.trip_component_id
                    ? parseInt(formData.trip_component_id, 10)
                    : undefined,
                package_component_id: parseInt(formData.package_component_id, 10),
                merchant_allocation_id: formData.merchant_allocation_id
                    ? parseInt(formData.merchant_allocation_id, 10)
                    : null,
                merchant_id: formData.merchant_id ? parseInt(formData.merchant_id, 10) : null,
                pax_count: parseInt(formData.pax_count, 10),
                unit_price: parseFloat(formData.unit_price),
                service_start: formData.service_start ? new Date(formData.service_start).toISOString() : null,
                service_end: formData.service_end ? new Date(formData.service_end).toISOString() : null,
                cancellation_date: formData.cancellation_date
                    ? new Date(formData.cancellation_date).toISOString()
                    : null,
            };

            await onSave(payload);
            onClose();
            setFormData(emptyForm());
        } catch (error) {
            console.error('Error saving Merchant Order Line:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Merchant Order Line',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '90%', md: '900px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Merchant Order Line</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Line Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        helperText={formErrors.name || 'Leave blank to auto-generate MOL-#####'}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={orderOptions}
                                        value={selectedOrderOption}
                                        loading={loadingOrders}
                                        openOnFocus
                                        autoHighlight
                                        clearOnEscape
                                        onChange={(_event, next) => applyOrder(next?.order || null)}
                                        getOptionLabel={(option) => option?.label || ''}
                                        isOptionEqualToValue={(option, value) =>
                                            String(option?.value) === String(value?.value)
                                        }
                                        ListboxProps={{ sx: { maxHeight: 240 } }}
                                        slotProps={autocompleteSlotProps}
                                        noOptionsText="No bookings found"
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Order / Booking"
                                                required
                                                margin="normal"
                                                variant="outlined"
                                                placeholder="Search booking"
                                                error={!!formErrors.travel_order_id}
                                                helperText={formErrors.travel_order_id || ''}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={tripLogisticsOptions}
                                        value={selectedTripLogistics}
                                        loading={loadingPackageComponents}
                                        disabled={!formData.travel_order_id}
                                        openOnFocus
                                        autoHighlight
                                        clearOnEscape
                                        onChange={(_event, next) => {
                                            setFormData((prev) => ({
                                                ...prev,
                                                package_component_id: next ? String(next.value) : '',
                                                merchant_allocation_id: '',
                                                merchant_id: '',
                                            }));
                                            setFormErrors((prev) => ({
                                                ...prev,
                                                package_component_id: '',
                                                merchant_id: '',
                                            }));
                                        }}
                                        getOptionLabel={(option) => option?.label || ''}
                                        isOptionEqualToValue={(option, value) =>
                                            String(option?.value) === String(value?.value)
                                        }
                                        ListboxProps={{ sx: { maxHeight: 240 } }}
                                        slotProps={autocompleteSlotProps}
                                        noOptionsText={
                                            formData.travel_order_id
                                                ? 'No trip logistics on this booking package'
                                                : 'Select a booking first'
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Trip Logistics"
                                                required
                                                margin="normal"
                                                variant="outlined"
                                                placeholder={
                                                    formData.travel_order_id
                                                        ? 'Search trip logistics'
                                                        : 'Select a booking first'
                                                }
                                                error={!!formErrors.package_component_id}
                                                helperText={
                                                    formErrors.package_component_id
                                                    || (formData.travel_order_id
                                                        ? 'Services from the selected booking package'
                                                        : 'Select a booking to load its trip logistics')
                                                }
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Merchant Allocation</InputLabel>
                                        <Select
                                            name="merchant_allocation_id"
                                            value={formData.merchant_allocation_id}
                                            onChange={(e) => {
                                                const alloc = allocationOptions.find(
                                                    (ma) => String(ma.id) === String(e.target.value)
                                                );
                                                applyAllocation(alloc || null);
                                            }}
                                            label="Merchant Allocation"

                                            disabled={loadingAllocations || !formData.travel_order_id}
                                        >
                                            <MenuItem value=""><em>Select a Merchant Allocation</em></MenuItem>
                                            {allocationOptions.map((ma) => (
                                                <MenuItem key={ma.id} value={ma.id}>{ma.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Merchant</InputLabel>
                                        <Select
                                            name="merchant_id"
                                            value={formData.merchant_id}
                                            onChange={handleInputChange}
                                            label="Merchant"
                                            disabled={loadingMerchants || !formData.package_component_id}
                                        >
                                            <MenuItem value="">
                                                <em>
                                                    {formData.package_component_id
                                                        ? 'Select a Merchant'
                                                        : 'Select trip logistics first'}
                                                </em>
                                            </MenuItem>
                                            {filteredMerchants.map((m) => (
                                                <MenuItem key={m.id} value={m.id}>
                                                    {m.name}{merchantHasDmc(m) ? ' (DMC)' : ''}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <MDTypography variant="caption" color="text" sx={{ display: 'block', mt: -1, ml: 0.5 }}>
                                        {formData.package_component_id
                                            ? 'Matching trip logistics service type / linked assets, plus all DMC merchants'
                                            : 'Choose trip logistics to load matching merchants'}
                                    </MDTypography>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Pax Count"
                                        name="pax_count"
                                        type="number"
                                        value={formData.pax_count}
                                        onChange={handleInputChange}
                                        error={!!formErrors.pax_count}
                                        helperText={formErrors.pax_count}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Unit Price"
                                        name="unit_price"
                                        type="number"
                                        value={formData.unit_price}
                                        onChange={handleInputChange}
                                        error={!!formErrors.unit_price}
                                        helperText={formErrors.unit_price}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Service Start"
                                        name="service_start"
                                        type="datetime-local"
                                        value={formData.service_start}
                                        onChange={handleInputChange}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Service End"
                                        name="service_end"
                                        type="datetime-local"
                                        value={formData.service_end}
                                        onChange={handleInputChange}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" required>
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            label="Status"

                                        >
                                            <MenuItem value="Waiting For Merchant Acknowledgment">Waiting For Merchant Acknowledgment</MenuItem>
                                            <MenuItem value="Booked">Booked</MenuItem>
                                            <MenuItem value="Cancelled">Cancelled</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Merchant Confirmation No"
                                        name="merchant_confirmation_no"
                                        value={formData.merchant_confirmation_no}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Cancellation Date"
                                        name="cancellation_date"
                                        type="datetime-local"
                                        value={formData.cancellation_date}
                                        onChange={handleInputChange}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Merchant Notes"
                                        name="merchant_notes"
                                        value={formData.merchant_notes}
                                        onChange={handleInputChange}
                                        multiline
                                        rows={3}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }} disabled={isSaving}>
                                    Cancel
                                </MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Merchant Order Line'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={4000}
                onClose={() => setToast((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setToast((prev) => ({ ...prev, open: false }))}
                    severity={toast.severity}
                    sx={{ width: '100%' }}
                >
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewMerchantOrderLineModal;
